import { useCallback, useState } from 'react'

import { Context, useQuery } from '@apollo/client'
import classNames from 'classnames'

import Advertisement, { AdPage, AdType, PlaceSectionForAd } from '@web/components/Advertisement/Advertisement'
import CheckInButton from '@web/components/CheckInButton/CheckInButton'
import Footer from '@web/components/Footer/Footer'
import Layout from '@web/components/Layout'
import Loading from '@web/components/Loading'
import NoContent from '@web/components/NoContent'
import { NoContentTypes } from '@web/components/NoContent/type'
import PlaceSection from '@web/components/PlaceSection/PlaceSection'
import { gql } from '@web/gql'
import { UserGrade } from '@web/gql/graphql'
import useAppBgColor, { AppBgColors } from '@web/hooks/useAppBgColor'
import useMe, { meQuery } from '@web/hooks/useMe'
import usePageInfo, { pageOwnerQuery } from '@web/hooks/usePageInfo'
import useResizeObserver from '@web/hooks/useResizeObserver'
import { initializeApollo } from '@web/libs/apollo/client'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import routes from '@web/libs/constants/routes'
import nClicks from '@web/libs/nlog/click/codes'
import { isValidObjectId } from '@web/libs/utils'
import UserClips from '@web/pages/[objectId]/review/components/UserClips/UserClips'
import { getOGTag } from '@web/pages/[objectId]/review/index.helper'
import ReviewHeader from '@web/pages/review/components/ReviewHeader'
import ReviewProfile from '@web/pages/review/components/ReviewProfile'

import ReviewTab from './components/ReviewTab/ReviewTab'
import UserThemelists from './components/UserThemelists/UserThemelists'
import styles from './index.module.scss'

export enum ReviewTabRowCount {
  TWO = 2,
  THREE = 3,
}

const myReviewGroupsCountQuery = gql(`
  query MyReviewGroupsCount($input: UserReviewGroupsInput!) {
    userReviewGroupsCount(input: $input)
  }
`)

const UserClipsCountQuery = gql(`
  query UserClipsCountQuery($input: UserClipsInput!) {
    userClipsCount(input: $input)
  }
`)

const ReviewTabPage = () => {
  useAppBgColor(AppBgColors.GRAY)
  const { pageOwner, isMyPage, loading: pageInfoLoading } = usePageInfo()
  const { me, loading: meLoading, login } = useMe()

  const [rowCount, setRowCount] = useState(ReviewTabRowCount.TWO)

  const setRowCountOnResize = useCallback((entry: ResizeObserverEntry) => {
    const smallSizeScreen = entry.contentRect.width < parseInt(styles.reviewtabMinWidth ?? 0)
    if (smallSizeScreen) setRowCount(ReviewTabRowCount.TWO)
    else setRowCount(ReviewTabRowCount.THREE)
  }, [])

  useResizeObserver({ callback: setRowCountOnResize, isRoot: true })

  const ogTag = getOGTag(pageOwner)

  // userStat 싱크느려서 본인뷰 + 리뷰수 통계가 0인 경우 리뷰카운트 조회하여 실제 리뷰수 확인
  const { data, loading } = useQuery(myReviewGroupsCountQuery, {
    variables: {
      input: {
        userIdno: pageOwner?.idno!,
      },
    },
    skip: !pageOwner?.idno || !isMyPage || !!pageOwner?.stat.reviewCount,
  })

  const { data: clipData, loading: clipLoading } = useQuery(UserClipsCountQuery, {
    variables: { input: { channelId: pageOwner?.id! } },
    skip: !pageOwner?.id,
  })
  const clipCount = clipData?.userClipsCount ?? 0

  if (loading || meLoading || pageInfoLoading || clipLoading) {
    return <Loading isPageType />
  }

  if (isMyPage && !login) {
    return (
      <Layout metaTagProps={{ title: '네이버 MY플레이스' }} className="place_simple_wrapper_new">
        <ReviewHeader />
        <NoContent type={NoContentTypes.REVIEW_NOT_AVAILABLE} />
        <Footer />
        <CheckInButton clickCode={nClicks.lnb.WRITE_REVIEW} />
      </Layout>
    )
  }

  if (!isMyPage && !pageOwner?.stat.reviewCount && !pageOwner?.isThemeContentProvider && !clipCount) {
    return (
      <Layout metaTagProps={ogTag} className="place_simple_wrapper_new">
        <ReviewHeader />
        <ReviewProfile />
        <NoContent type={NoContentTypes.REVIEW_GUEST} />
        <Footer />
      </Layout>
    )
  }

  const reviewGroupsCount = data?.userReviewGroupsCount || pageOwner?.stat.reviewCount || clipCount
  const isHideTheme = !data?.userReviewGroupsCount && !pageOwner?.stat.reviewCount && !pageOwner?.isThemeContentProvider
  if (isMyPage && !reviewGroupsCount && !pageOwner?.isThemeContentProvider) {
    return (
      <Layout metaTagProps={ogTag} className="place_simple_wrapper_new">
        <ReviewHeader />
        <ReviewProfile />
        <NoContent
          type={me?.currentGrade === UserGrade.Black ? NoContentTypes.REVIEW : NoContentTypes.REVIEW_NOT_AVAILABLE}
        />
        <CheckInButton clickCode={nClicks.lnb.WRITE_REVIEW} />
        <Footer />
      </Layout>
    )
  }

  return (
    <Layout metaTagProps={ogTag} className="place_simple_wrapper_new">
      <ReviewHeader />
      <ReviewProfile />
      {/* 광고 섹션 */}
      <PlaceSectionForAd isSmartChannel>
        <Advertisement
          adSlotElementId="review_tab_top_ad"
          type={AdType.SMART_CHANNEL}
          page={isMyPage ? AdPage.REVIEW_TAB_MINE : AdPage.REVIEW_TAB_OTHERS}
          userIdno={pageOwner?.idno}
        />
      </PlaceSectionForAd>
      {/* 클립 섹션 */}
      {clipCount > 0 && (
        <PlaceSection className={styles.card_wrapper}>
          <UserClips />
        </PlaceSection>
      )}
      {/* 테마리스트 섹션 */}
      {!isHideTheme && (
        <PlaceSection className={styles.card_wrapper}>
          <UserThemelists />
        </PlaceSection>
      )}
      {/* 리뷰 섹션 */}
      {!pageOwner?.isThemeContentProvider && (
        <PlaceSection className={classNames(styles.card_wrapper, styles.type_review)}>
          <ReviewTab rowCount={rowCount} />
        </PlaceSection>
      )}
      {isMyPage && <CheckInButton clickCode={nClicks.lnb.WRITE_REVIEW} />}
      <Footer />
    </Layout>
  )
}

export default ReviewTabPage

export async function getServerSideProps(context: Context) {
  const objectId = context.params?.objectId?.toString()
  if (!objectId) {
    return {
      redirect: {
        destination: routes.HOME_MY_REVIEW,
        permanent: false,
      },
    }
  }

  if (!isValidObjectId(objectId)) {
    return {
      redirect: {
        destination: routes.HOME,
        permanent: false,
      },
    }
  }

  const apolloClient = initializeApollo({ context })

  const { data } = await apolloClient.query({
    query: meQuery,
  })

  const { MeSucceed } = extractByTypename(data.me)

  if (objectId === MeSucceed?.user.id) {
    return {
      redirect: {
        destination: routes.HOME_MY_REVIEW,
        permanent: false,
      },
    }
  }

  await apolloClient.query({ query: pageOwnerQuery, variables: { id: objectId } })

  await apolloClient.query({
    query: UserClipsCountQuery,
    variables: { input: { channelId: objectId } },
  })

  return {
    props: {
      initialState: apolloClient.cache.extract(),
    },
  }
}
